export const LANDING_TITLE =
  'JobMockAI | AI-Powered Hiring, ATS & Interview Preparation Platform';

export const CANDIDATE_PAGE_TITLE =
  'JobMockAI for Candidates | Jobs, Resume Matching & Interview Prep';

export const CANDIDATE_PAGE_DESCRIPTION =
  'Practice AI mock interviews, connect with peers, and prepare with verified experts. Search jobs, upload your resume, and land your next role with JobMockAI.';

export const CTAS = {
  demo: { label: 'Schedule ATS Demo', to: '/demo', variant: 'demoPrimary' },
  demoAlt: { label: 'Book ATS Demo', to: '/demo', variant: 'demoPrimary' },
  signup: { label: 'Get started free', to: '/signup', variant: 'secondary' },
  practice: { label: 'Start practicing', to: '/signup?redirect=/interview/setup', variant: 'candidate' },
  interviewer: { label: 'Become an interviewer', to: '/become-an-interviewer', variant: 'interviewer' },
  candidate: { label: 'Get Started', to: '/candidate', variant: 'candidate' },
  browseJobs: { label: 'Browse Jobs', to: '/jobs', variant: 'candidate' },
  createAccount: { label: 'Create account', to: '/signup', variant: 'secondary' },
  signIn: { label: 'Sign in', to: '/login', variant: 'text' },
  joinCommunity: {
    label: 'Join Community',
    to: '/signup?redirect=/community/feed',
    variant: 'candidate',
  },
  browseExperts: {
    label: 'Browse Experts',
    to: '/signup?redirect=/marketplace',
    variant: 'candidate',
  },
  ssbPrep: {
    label: 'SSB & alumni prep',
    to: '/signup?redirect=/campus/placement',
    variant: 'candidate',
  },
};

export const NAV_LINKS = [
  { label: 'Home', to: '/landing' },
  { label: 'Product tours', to: '/product-tours' },
  { label: 'Candidates', to: '/candidate' },
  { label: 'Recruiters', to: '/landing#recruiters' },
  { label: 'Interviewers', to: '/become-an-interviewer' },
  { label: 'Pricing', to: '/pricing' },
];

export const HERO_HEADLINES = [
  'Practice until you are interview-ready.',
  'Learn with peers. Book experts when it counts.',
];

export const HERO_SUBHEADLINE =
  'JobMockAI helps candidates prepare with AI mock interviews, a career community, and verified expert coaches — on one platform.';

export const CANDIDATE_PILLARS = [
  {
    title: 'AI Mock Interviews',
    description:
      'Practice anytime with AI that adapts to your role, gives instant feedback, and builds confidence.',
    cta: CTAS.practice,
  },
  {
    title: 'Peer-to-Peer Practice',
    description:
      'Connect with other job seekers, schedule mutual mock sessions, and learn from diverse perspectives.',
    cta: CTAS.joinCommunity,
  },
  {
    title: 'Expert Interview Network',
    description:
      'Practice with verified professionals from our growing interviewer network — engineers, managers, and domain experts.',
    cta: CTAS.browseExperts,
  },
  {
    title: 'Campus & SSB prep',
    description:
      'Placement path plus SSB-style Personal Interview practice — and book alumni coaches when you want a live mock.',
    cta: CTAS.ssbPrep,
  },
];

export const PATH_CARDS = [
  {
    id: 'candidate',
    title: "I'm a Candidate",
    bullets: [
      'Search jobs',
      'Upload resume',
      'AI-powered interview preparation',
      'Practice mock interviews',
      'Get personalized feedback',
      'Improve interview readiness',
    ],
    cta: CTAS.candidate,
    popular: false,
  },
  {
    id: 'recruiter',
    title: "I'm a Recruiter",
    bullets: [
      'Post jobs',
      'Manage applicants',
      'AI resume matching',
      'Candidate ranking',
      'ATS dashboard',
      'Faster hiring',
    ],
    cta: CTAS.demoAlt,
    popular: true,
  },
  {
    id: 'interviewer',
    title: "I'm an Interviewer",
    bullets: [
      'Conduct mock interviews',
      'Earn additional income',
      'Build your professional profile',
      'Flexible schedule',
      'Help candidates succeed',
    ],
    cta: CTAS.interviewer,
    popular: false,
  },
];

export const HOW_IT_WORKS_STEPS = [
  { title: 'Recruiter posts a job', description: 'Create job openings and publish to your talent pipeline.' },
  { title: 'Candidates apply', description: 'Qualified candidates discover roles and submit applications.' },
  { title: 'AI matches resumes', description: 'Resumes are compared against job descriptions automatically.' },
  { title: 'Candidates prepare', description: 'Mock interviews and feedback improve readiness before submissions.' },
  { title: 'Recruiters hire faster', description: 'Better-matched, interview-ready candidates accelerate hiring.' },
];

export const ATS_FEATURES = [
  {
    title: 'AI Resume Matching',
    description: 'Automatically compare resumes against job descriptions and identify top candidates.',
  },
  {
    title: 'Candidate Ranking',
    description: 'Rank candidates based on skills, experience, and job fit.',
  },
  {
    title: 'Applicant Tracking',
    description: 'Manage the hiring pipeline from a single dashboard.',
  },
  {
    title: 'Multi-Recruiter Support',
    description: 'Invite recruiters and hiring managers to collaborate.',
  },
  {
    title: 'Job Posting',
    description: 'Create and manage job openings efficiently.',
  },
  {
    title: 'Interview Readiness Platform',
    description: 'Help candidates prepare before client submissions.',
  },
];

export const CANDIDATE_FEATURES = [
  { title: 'AI Mock Interviews', description: 'Start with realistic AI sessions — voice interaction and detailed feedback reports. Practice free, anytime.' },
  { title: 'Peer-to-Peer Practice', description: 'Practice with real people, not just AI. Connect with fellow candidates and give each other actionable feedback.' },
  { title: 'Expert Interview Network', description: 'Join our growing network of verified interviewers — senior engineers, hiring managers, and domain experts.' },
  { title: 'Interview Feedback Reports', description: 'Receive detailed, actionable feedback after every session.' },
  { title: 'Interview Recording', description: 'Review your performance and track improvement over time.' },
  { title: 'Resume Matching', description: 'See how your resume aligns with open roles and job descriptions.' },
];

export const INTERVIEWER_BENEFITS = [
  'Flexible schedule',
  'Remote interviews',
  'Earn money',
  'Build your brand',
  'Help candidates succeed',
];

export const INTERVIEWER_NETWORK_DESCRIPTION =
  'Experienced professionals can conduct mock interviews, mentor candidates, build their reputation, and earn additional income.';

export const TESTIMONIALS = [
  {
    role: 'candidate',
    quote:
      'After three mock interviews on JobMockAI, I walked into my onsite feeling prepared. The feedback on my STAR answers was exactly what I needed.',
    name: 'Priya Sharma',
    title: 'Software Engineer · Hired at a Series B startup',
    rating: 5,
  },
  {
    role: 'recruiter',
    quote:
      'The AI resume matching cut our shortlist time in half. We submit better-prepared candidates and clients notice the difference.',
    name: 'Marcus Chen',
    title: 'Senior Recruiter · Staffing Agency',
    rating: 5,
  },
  {
    role: 'interviewer',
    quote:
      'I conduct mock sessions on evenings and weekends. It is flexible, rewarding, and has helped me grow my professional network.',
    name: 'Elena Rodriguez',
    title: 'Engineering Manager · JobMockAI Interviewer',
    rating: 5,
  },
];

export const HERO_FLOW_NODES = [
  { label: 'Candidate', key: 'candidate' },
  { label: 'Interview', key: 'interview' },
  { label: 'Hire', key: 'hire' },
  { label: 'Recruiter', key: 'recruiter' },
];

export const EXPERIENCE_CTA = {
  headline: 'Ready to experience your own AI mock interview?',
  primary: { label: 'Start Free Mock Interview', to: '/signup', variant: 'candidate' },
  secondary: { label: 'Learn More', to: '/candidate', variant: 'secondary' },
};
